import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useLocale, useT } from '@/shared/i18n'
import {
  ASPECT_SIZES,
  CollageGuardrailError,
  MAX_IMAGES,
  MAX_SOURCE_PIXELS,
  MAX_TOTAL_BYTES,
  MIN_IMAGES,
  buildLayout,
  moveItem,
  validateExportResources,
  type AspectRatioKey,
  type FocalPoint,
  type GridPresetKey,
  type ImageMeta,
  type OutputFormat,
} from '@/features/photo-collage/model/collage'
import {
  FilePreflightError,
  hasSupportedCollageFileMetadata,
  inspectCollageFile,
} from '@/features/photo-collage/model/filePreflight'
import {
  startCollageWorkerExport,
  type ExportFailureCode,
  type WorkerExportOwner,
} from '@/features/photo-collage/model/workerExport'
import type { CollageWorkerRequest } from '@/features/photo-collage/model/types'
import { ToolHero } from '@/shared/ui/ToolHero'

interface CollageAsset {
  id: number
  file: File
  url: string
  meta: ImageMeta
  focal: FocalPoint
}

interface ExportResult {
  blob: Blob
  url: string
  format: OutputFormat
}

const UTILITY_KEY = 'photo-collage'

const COPY = {
  ru: {
    eyebrow: 'Коллаж из фото',
    title: 'Сделать коллаж из фото онлайн',
    description:
      'Добавьте от 2 до 12 фотографий, выберите сетку и скачайте JPG или PNG. Обработка и экспорт идут локально в браузере.',
    choose: 'Выбрать фотографии',
    drop: 'Перетащите JPG, PNG или WebP сюда',
    dropHint: 'От 2 до 12 изображений, до 50 MB каждое',
    editor: 'Фотографии и порядок',
    editorHint:
      'Перетаскивайте карточки или используйте Alt + стрелки. Выберите фото, чтобы настроить точку кадрирования.',
    empty: 'Добавьте минимум две фотографии',
    remove: 'Удалить',
    moveBefore: 'Раньше',
    moveAfter: 'Позже',
    focal: 'Кадрирование выбранного слота',
    focalX: 'Фокус по горизонтали',
    focalY: 'Фокус по вертикали',
    settings: 'Сетка и оформление',
    grid: 'Сетка',
    grids: {
      auto: 'Авто',
      columns: '2 колонки',
      'hero-left': 'Крупное слева',
      'hero-top': 'Крупное сверху',
    },
    aspect: 'Соотношение',
    aspects: {
      square: '1:1',
      portrait: '4:5',
      landscape: '16:9',
      classic: '3:2',
    },
    gap: 'Поля',
    radius: 'Скругление',
    background: 'Цвет фона',
    format: 'Формат',
    quality: 'Качество JPG',
    preview: 'Предпросмотр коллажа',
    export: 'Собрать коллаж',
    exporting: 'Собираем локально...',
    download: 'Скачать',
    reset: 'Начать заново',
    privacy:
      'Фотографии не отправляются на сервер. Preview использует локальные blob-ссылки, а полноразмерный экспорт выполняется в отдельном worker.',
    howToTitle: 'Как сделать коллаж',
    howTo: [
      'Добавьте от 2 до 12 фотографий JPG, PNG или WebP.',
      'Выберите сетку, порядок, пропорции, поля и точку кадрирования каждого слота.',
      'Соберите коллаж локально и скачайте JPG или PNG.',
    ],
    faqTitle: 'Вопросы',
    faq: [
      {
        q: 'Фотографии отправляются на сервер?',
        a: 'Нет. Исходные файлы остаются в браузере; сетевой upload и backend fallback отсутствуют.',
      },
      {
        q: 'Как изменить видимую область фотографии?',
        a: 'Выберите карточку и передвиньте горизонтальный и вертикальный фокус. Слот использует аккуратный cover-crop вокруг этой точки.',
      },
      {
        q: 'Зачем нужны ограничения?',
        a: 'Лимиты суммарных пикселей, памяти и времени защищают мобильную вкладку от зависания на слишком больших наборах.',
      },
    ],
    errors: {
      unsupported: 'Поддерживаются только JPG, PNG и WebP.',
      tooMany: 'Можно добавить не больше 12 фотографий.',
      tooLarge: 'Один из файлов больше 50 MB.',
      decode: 'Не удалось открыть одну из фотографий.',
      dimensions: 'Одна из фотографий слишком большая или имеет неверные размеры.',
      sourcePixels: 'Суммарное разрешение набора превышает безопасный лимит 72 MP.',
      needMore: 'Добавьте минимум две фотографии.',
      unsupportedBrowser: 'Для экспорта нужен браузер с Worker и OffscreenCanvas.',
      resource: 'Набор превышает безопасный лимит пикселей или памяти.',
      timeout: 'Экспорт занял слишком много времени. Уменьшите набор или разрешение фотографий.',
      export: 'Не удалось собрать коллаж. Попробуйте другой формат или меньший набор.',
    },
  },
  en: {
    eyebrow: 'Photo collage',
    title: 'Make a photo collage online',
    description:
      'Add 2 to 12 photos, choose a grid and download JPG or PNG. Processing and export stay local in your browser.',
    choose: 'Choose photos',
    drop: 'Drop JPG, PNG or WebP here',
    dropHint: '2 to 12 images, up to 50 MB each',
    editor: 'Photos and order',
    editorHint:
      'Drag cards or use Alt + arrow keys. Select a photo to adjust its crop focal point.',
    empty: 'Add at least two photos',
    remove: 'Remove',
    moveBefore: 'Move earlier',
    moveAfter: 'Move later',
    focal: 'Selected slot crop',
    focalX: 'Horizontal focus',
    focalY: 'Vertical focus',
    settings: 'Grid and style',
    grid: 'Grid',
    grids: {
      auto: 'Auto',
      columns: '2 columns',
      'hero-left': 'Hero left',
      'hero-top': 'Hero top',
    },
    aspect: 'Aspect ratio',
    aspects: { square: '1:1', portrait: '4:5', landscape: '16:9', classic: '3:2' },
    gap: 'Gap',
    radius: 'Corner radius',
    background: 'Background',
    format: 'Format',
    quality: 'JPG quality',
    preview: 'Collage preview',
    export: 'Create collage',
    exporting: 'Creating locally...',
    download: 'Download',
    reset: 'Start over',
    privacy:
      'Photos are not sent to a server. Preview uses local blob URLs and full-size export runs in a dedicated worker.',
    howToTitle: 'How to make a collage',
    howTo: [
      'Add 2 to 12 JPG, PNG or WebP photos.',
      'Choose the grid, order, ratio, gaps and focal point for each slot.',
      'Create the collage locally and download JPG or PNG.',
    ],
    faqTitle: 'Questions',
    faq: [
      {
        q: 'Are photos sent to a server?',
        a: 'No. Source files stay in the browser; there is no upload or backend fallback.',
      },
      {
        q: 'How do I change the visible area?',
        a: 'Select a card and adjust horizontal and vertical focus. The slot uses a cover crop around that point.',
      },
      {
        q: 'Why are there limits?',
        a: 'Total pixel, memory and time limits protect mobile tabs from freezing on oversized sets.',
      },
    ],
    errors: {
      unsupported: 'Only JPG, PNG and WebP are supported.',
      tooMany: 'You can add no more than 12 photos.',
      tooLarge: 'One of the files is larger than 50 MB.',
      decode: 'One of the photos could not be opened.',
      dimensions: 'One photo is too large or has invalid dimensions.',
      sourcePixels: 'The total source resolution exceeds the safe 72 MP limit.',
      needMore: 'Add at least two photos.',
      unsupportedBrowser: 'Export requires a browser with Worker and OffscreenCanvas.',
      resource: 'This set exceeds safe pixel or memory limits.',
      timeout: 'Export took too long. Use fewer or lower-resolution photos.',
      export: 'Could not create the collage. Try another format or a smaller set.',
    },
  },
} as const

function downloadResult(result: ExportResult): void {
  const anchor = document.createElement('a')
  anchor.href = result.url
  anchor.download = `photo-collage.${result.format === 'image/jpeg' ? 'jpg' : 'png'}`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

export function PhotoCollageTool() {
  const { locale } = useLocale()
  const translations = useT()
  const copy = COPY[locale]
  const t = { ...copy, howTo: translations.photoCollage.howTo, faq: translations.photoCollage.faq }
  const [assets, setAssets] = useState<CollageAsset[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [preset, setPreset] = useState<GridPresetKey>('auto')
  const [aspect, setAspect] = useState<AspectRatioKey>('square')
  const [gap, setGap] = useState(16)
  const [radius, setRadius] = useState(20)
  const [background, setBackground] = useState('#ffffff')
  const [format, setFormat] = useState<OutputFormat>('image/jpeg')
  const [quality, setQuality] = useState(0.92)
  const [dropActive, setDropActive] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ExportResult | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const exportOwnerRef = useRef<WorkerExportOwner | null>(null)
  const generationRef = useRef(0)
  const loadGenerationRef = useRef(0)
  const nextIdRef = useRef(1)
  const assetsRef = useRef<CollageAsset[]>([])
  const resultUrlRef = useRef<string | null>(null)

  useEffect(() => {
    assetsRef.current = assets
  }, [assets])

  const clearResult = useCallback(() => {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setResult(null)
  }, [])

  const invalidateExport = useCallback(() => {
    generationRef.current += 1
    if (exportOwnerRef.current) toolError(UTILITY_KEY, 'cancelled')
    const owner = exportOwnerRef.current
    exportOwnerRef.current = null
    owner?.cancel()
    setBusy(false)
    clearResult()
  }, [clearResult])

  useEffect(() => () => {
    generationRef.current += 1
    loadGenerationRef.current += 1
    exportOwnerRef.current?.cancel()
    exportOwnerRef.current = null
    for (const asset of assetsRef.current) URL.revokeObjectURL(asset.url)
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
  }, [])

  const selected = assets.find((asset) => asset.id === selectedId) ?? assets[0] ?? null
  const layout = useMemo(
    () => assets.length >= MIN_IMAGES ? buildLayout(assets.length, preset) : [],
    [assets.length, preset],
  )
  const size = ASPECT_SIZES[aspect]

  const addFiles = useCallback(async (incoming: FileList | File[]) => {
    setError(null)
    const loadGeneration = ++loadGenerationRef.current
    const files = Array.from(incoming)
    if (assetsRef.current.length + files.length > MAX_IMAGES) {
      setError(t.errors.tooMany)
      return
    }

    const candidates = files.filter(hasSupportedCollageFileMetadata)
    if (candidates.length > 0) invalidateExport()
    const accepted: CollageAsset[] = []
    for (const file of files) {
      try {
        const meta = await inspectCollageFile(file)
        if (loadGenerationRef.current !== loadGeneration) {
          for (const asset of accepted) URL.revokeObjectURL(asset.url)
          return
        }
        accepted.push({
          id: nextIdRef.current++,
          file,
          url: URL.createObjectURL(file),
          meta,
          focal: { x: 0.5, y: 0.5 },
        })
      } catch (failure) {
        if (loadGenerationRef.current !== loadGeneration) {
          for (const asset of accepted) URL.revokeObjectURL(asset.url)
          return
        }
        const code = failure instanceof FilePreflightError ? failure.code : 'decode_failed'
        if (code === 'unsupported' || code === 'animated_unsupported') {
          setError(t.errors.unsupported)
        } else if (code === 'file_too_large') {
          setError(t.errors.tooLarge)
        } else if (code === 'too_many_pixels' || code === 'invalid_container') {
          setError(t.errors.dimensions)
        } else {
          setError(t.errors.decode)
        }
      }
    }

    const next = [...assetsRef.current, ...accepted]
    const totalPixels = next.reduce(
      (sum, asset) => sum + asset.meta.width * asset.meta.height,
      0,
    )
    const totalBytes = next.reduce((sum, asset) => sum + asset.meta.bytes, 0)
    if (loadGenerationRef.current !== loadGeneration) {
      for (const asset of accepted) URL.revokeObjectURL(asset.url)
      return
    }
    if (totalBytes > MAX_TOTAL_BYTES) {
      for (const asset of accepted) URL.revokeObjectURL(asset.url)
      setError(t.errors.resource)
      return
    }
    if (totalPixels > MAX_SOURCE_PIXELS) {
      for (const asset of accepted) URL.revokeObjectURL(asset.url)
      setError(t.errors.sourcePixels)
      return
    }
    if (accepted.length > 0) {
      setAssets(next)
      setSelectedId((current) => current ?? accepted[0]?.id ?? null)
    }
  }, [invalidateExport, t.errors])

  const move = useCallback((from: number, to: number) => {
    if (from === to) return
    setAssets((current) => moveItem(current, from, to))
    invalidateExport()
  }, [invalidateExport])

  const remove = useCallback((id: number) => {
    setAssets((current) => {
      const target = current.find((asset) => asset.id === id)
      if (target) URL.revokeObjectURL(target.url)
      const next = current.filter((asset) => asset.id !== id)
      setSelectedId((selectedAsset) =>
        selectedAsset === id ? next[0]?.id ?? null : selectedAsset,
      )
      return next
    })
    invalidateExport()
  }, [invalidateExport])

  const updateFocal = useCallback((axis: keyof FocalPoint, value: number) => {
    if (!selected) return
    setAssets((current) => current.map((asset) =>
      asset.id === selected.id
        ? { ...asset, focal: { ...asset.focal, [axis]: value } }
        : asset,
    ))
    invalidateExport()
  }, [invalidateExport, selected])

  const reset = useCallback(() => {
    loadGenerationRef.current += 1
    invalidateExport()
    for (const asset of assetsRef.current) URL.revokeObjectURL(asset.url)
    setAssets([])
    setSelectedId(null)
    setError(null)
  }, [invalidateExport])

  const exportCollage = useCallback(() => {
    setError(null)
    if (assets.length < MIN_IMAGES) {
      setError(t.errors.needMore)
      return
    }
    if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') {
      setError(t.errors.unsupportedBrowser)
      toolError(UTILITY_KEY, 'unsupported_browser')
      return
    }

    try {
      validateExportResources(assets.map((asset) => asset.meta), size)
    } catch (guardrail) {
      setError(t.errors.resource)
      toolError(
        UTILITY_KEY,
        guardrail instanceof CollageGuardrailError ? `resource_${guardrail.code}` : 'resource_limit',
      )
      return
    }

    invalidateExport()
    const generation = generationRef.current
    setBusy(true)
    toolInvoke(UTILITY_KEY)
    const request: CollageWorkerRequest = {
      items: assets.map((asset) => ({
        file: asset.file,
        meta: asset.meta,
        focal: asset.focal,
      })),
      preset,
      width: size.width,
      height: size.height,
      gap,
      radius,
      background,
      format,
      quality,
    }

    const analyticsCode = (code: ExportFailureCode): string => {
      if (code === 'guardrail') return 'resource_limit'
      if (code === 'timeout') return 'timeout'
      if (code === 'cancelled') return 'cancelled'
      if (code === 'decode_failed') return 'decode_failed'
      if (code === 'canvas_failed') return 'canvas_failed'
      if (code === 'export_failed') return 'export_failed'
      return 'worker_failed'
    }
    let completedSynchronously = false
    const owner = startCollageWorkerExport(request, {
      success: (blob, durationMs) => {
        completedSynchronously = true
        if (generationRef.current !== generation) return
        exportOwnerRef.current = null
        setBusy(false)
        clearResult()
        const url = URL.createObjectURL(blob)
        resultUrlRef.current = url
        setResult({ blob, url, format })
        toolSuccess(UTILITY_KEY, durationMs)
      },
      error: (code) => {
        completedSynchronously = true
        if (generationRef.current !== generation) return
        exportOwnerRef.current = null
        setBusy(false)
        setError(code === 'timeout' ? t.errors.timeout : t.errors.export)
        toolError(UTILITY_KEY, analyticsCode(code))
      },
    })
    if (!completedSynchronously) exportOwnerRef.current = owner
  }, [
    assets,
    background,
    format,
    gap,
    invalidateExport,
    preset,
    quality,
    radius,
    clearResult,
    size,
    t.errors,
  ])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero eyebrow={t.eyebrow} title={t.title} description={t.description} />

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div
            className={cn(
              'rounded-lg border-2 border-dashed p-5 text-center transition-colors',
              dropActive ? 'border-primary bg-primary/5' : 'border-border bg-muted/20',
            )}
            onDragOver={(event) => {
              event.preventDefault()
              setDropActive(true)
            }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDropActive(false)
              void addFiles(event.dataTransfer.files)
            }}
          >
            <div className="hidden font-medium [@media(pointer:fine)]:block">{t.drop}</div>
            <div className="mt-1 text-sm text-muted-foreground">{t.dropHint}</div>
            <input
              ref={inputRef}
              className="sr-only"
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              multiple
              onChange={(event) => {
                if (event.target.files) void addFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <Button className="mt-4" type="button" onClick={() => inputRef.current?.click()}>
              {t.choose}
            </Button>
          </div>
          {error ? (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
              {error}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold">{t.preview}</h2>
              <div
                className="relative mx-auto mt-4 w-full max-w-2xl overflow-hidden rounded-lg border shadow-sm"
                style={{ aspectRatio: `${size.width} / ${size.height}`, background }}
              >
                {layout.map((cell, index) => {
                  const asset = assets[index]
                  if (!asset) return null
                  const previewGap = Math.min(gap / 8, 8)
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      className={cn(
                        'absolute overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        selected?.id === asset.id && 'ring-2 ring-primary',
                      )}
                      style={{
                        left: `${cell.x * 100}%`,
                        top: `${cell.y * 100}%`,
                        width: `${cell.width * 100}%`,
                        height: `${cell.height * 100}%`,
                        padding: `${previewGap}px`,
                      }}
                      onClick={() => setSelectedId(asset.id)}
                      aria-label={`${index + 1}`}
                    >
                      <img
                        className="h-full w-full object-cover"
                        src={asset.url}
                        alt=""
                        style={{
                          borderRadius: `${Math.min(radius / 8, 16)}px`,
                          objectPosition: `${asset.focal.x * 100}% ${asset.focal.y * 100}%`,
                        }}
                      />
                    </button>
                  )
                })}
                {assets.length < MIN_IMAGES ? (
                  <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-muted-foreground">
                    {t.empty}
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold">{t.editor}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t.editorHint}</p>
              <ol className="mt-4 grid gap-3 sm:grid-cols-2">
                {assets.map((asset, index) => (
                  <li
                    key={asset.id}
                    draggable
                    className={cn(
                      'rounded-md border bg-background p-2',
                      selected?.id === asset.id && 'border-primary ring-1 ring-primary',
                    )}
                    onDragStart={(event) => event.dataTransfer.setData('text/plain', String(index))}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault()
                      const from = Number(event.dataTransfer.getData('text/plain'))
                      if (Number.isInteger(from)) move(from, index)
                    }}
                    onKeyDown={(event) => {
                      if (!event.altKey) return
                      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                        event.preventDefault()
                        move(index, Math.max(0, index - 1))
                      }
                      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                        event.preventDefault()
                        move(index, Math.min(assets.length - 1, index + 1))
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 text-left"
                      onClick={() => setSelectedId(asset.id)}
                    >
                      <img className="h-14 w-14 rounded object-cover" src={asset.url} alt="" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{index + 1}</span>
                        <span className="block text-xs text-muted-foreground">
                          {asset.meta.width} x {asset.meta.height}
                        </span>
                      </span>
                    </button>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      >
                        {t.moveBefore}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={index === assets.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        {t.moveAfter}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(asset.id)}>
                        {t.remove}
                      </Button>
                    </div>
                  </li>
                ))}
              </ol>

              {selected ? (
                <fieldset className="mt-5 rounded-md border p-4">
                  <legend className="px-1 text-sm font-medium">{t.focal}</legend>
                  <label className="block text-sm">
                    {t.focalX}
                    <input
                      className="mt-2 w-full accent-primary"
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={selected.focal.x}
                      onChange={(event) => updateFocal('x', Number(event.target.value))}
                    />
                  </label>
                  <label className="mt-3 block text-sm">
                    {t.focalY}
                    <input
                      className="mt-2 w-full accent-primary"
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={selected.focal.y}
                      onChange={(event) => updateFocal('y', Number(event.target.value))}
                    />
                  </label>
                </fieldset>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit">
          <CardContent className="space-y-5 p-4 sm:p-6">
            <h2 className="text-xl font-semibold">{t.settings}</h2>
            <fieldset>
              <legend className="text-sm font-medium">{t.grid}</legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(Object.keys(t.grids) as GridPresetKey[]).map((key) => (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    variant={preset === key ? 'default' : 'outline'}
                    onClick={() => {
                      setPreset(key)
                      invalidateExport()
                    }}
                  >
                    {t.grids[key]}
                  </Button>
                ))}
              </div>
            </fieldset>
            <label className="block text-sm font-medium">
              {t.aspect}
              <select
                className="mt-2 w-full rounded-md border bg-background px-3 py-2"
                value={aspect}
                onChange={(event) => {
                  setAspect(event.target.value as AspectRatioKey)
                  invalidateExport()
                }}
              >
                {(Object.keys(t.aspects) as AspectRatioKey[]).map((key) => (
                  <option key={key} value={key}>{t.aspects[key]}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium">
              {t.gap}: {gap}px
              <input
                className="mt-2 w-full accent-primary"
                type="range"
                min="0"
                max="64"
                value={gap}
                onChange={(event) => {
                  setGap(Number(event.target.value))
                  invalidateExport()
                }}
              />
            </label>
            <label className="block text-sm font-medium">
              {t.radius}: {radius}px
              <input
                className="mt-2 w-full accent-primary"
                type="range"
                min="0"
                max="96"
                value={radius}
                onChange={(event) => {
                  setRadius(Number(event.target.value))
                  invalidateExport()
                }}
              />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm font-medium">
              {t.background}
              <input
                type="color"
                value={background}
                onChange={(event) => {
                  setBackground(event.target.value)
                  invalidateExport()
                }}
              />
            </label>
            <label className="block text-sm font-medium">
              {t.format}
              <select
                className="mt-2 w-full rounded-md border bg-background px-3 py-2"
                value={format}
                onChange={(event) => {
                  setFormat(event.target.value as OutputFormat)
                  invalidateExport()
                }}
              >
                <option value="image/jpeg">JPG</option>
                <option value="image/png">PNG</option>
              </select>
            </label>
            {format === 'image/jpeg' ? (
              <label className="block text-sm font-medium">
                {t.quality}: {Math.round(quality * 100)}%
                <input
                  className="mt-2 w-full accent-primary"
                  type="range"
                  min="0.6"
                  max="1"
                  step="0.01"
                  value={quality}
                  onChange={(event) => {
                    setQuality(Number(event.target.value))
                    invalidateExport()
                  }}
                />
              </label>
            ) : null}
            <Button
              className="w-full"
              disabled={assets.length < MIN_IMAGES || busy}
              onClick={exportCollage}
            >
              {busy ? t.exporting : t.export}
            </Button>
            {result ? (
              <Button className="w-full" variant="outline" onClick={() => downloadResult(result)}>
                {t.download} {result.format === 'image/jpeg' ? 'JPG' : 'PNG'}
              </Button>
            ) : null}
            {assets.length > 0 ? (
              <Button className="w-full" variant="ghost" onClick={reset}>
                {t.reset}
              </Button>
            ) : null}
            <p className="text-xs leading-5 text-muted-foreground">{t.privacy}</p>
          </CardContent>
        </Card>
      </div>

      <section className="mt-8 grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <h2 className="text-xl font-semibold">{t.howToTitle}</h2>
            <ol className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
              {t.howTo.map((step, index) => <li key={step}>{index + 1}. {step}</li>)}
            </ol>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h2 className="text-xl font-semibold">{t.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {t.faq.map((item) => (
                <div key={item.question}>
                  <h3 className="font-medium">{item.question}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
      <nav className="mt-6 flex flex-wrap justify-center gap-3 text-sm" aria-label={locale === 'ru' ? 'Связанные инструменты' : 'Related tools'}>
        <a className="rounded-md border bg-card px-3 py-2 hover:border-primary" href="/obrezat-foto">
          {locale === 'ru' ? 'Обрезать фото' : 'Crop photo'}
        </a>
        <a className="rounded-md border bg-card px-3 py-2 hover:border-primary" href="/izmenit-razmer-foto">
          {locale === 'ru' ? 'Изменить размер' : 'Resize photo'}
        </a>
        <a className="rounded-md border bg-card px-3 py-2 hover:border-primary" href="/szhat-izobrazhenie">
          {locale === 'ru' ? 'Сжать изображение' : 'Compress image'}
        </a>
      </nav>
    </div>
  )
}
